import { SVGPath, ViewBox } from '@acies/core';
import { ICONS } from './icons';
import theme from './theme';

/**
 * The Icon Component is used for displaying icons.
 *
 * The name property accepts the keyof typeof ICONS exported from the icons.ts file.
 *
 * The source of the path commands is located here:
 *
 * @see {@link https://www.figma.com/design/iTYOILvuIyHaY7lPxTJ1W6/Wireframing?node-id=470-4814}
 */
export default class Icon extends SVGPath {
	public static override observedAttributes = [...SVGPath.observedAttributes, 'name', 'variant'];
	/** @param value The keyof typeof ICONS that has the path commands value or null */
	public set name(value: keyof typeof ICONS | null) {
		assertIsValidIconName(value);
		this.#name = value;
		this.#nameChanged = true;
		this.invalidateProperties();
	}

	public get name(): keyof typeof ICONS | null {
		return this.#name;
	}

	/**
	 * The variant property controls which Icon styles you want to apply to the Icon.
	 *
	 * @param value Default 'default'
	 */
	public set variant(value: 'default' | 'disabled' | 'error' | 'focused' | 'warning') {
		if (this.#variant === value) {
			return;
		}
		assertIsValidVariant(value);
		this.#variant = value;
		this.#variantChanged = true;
		this.invalidateProperties();
	}

	public get variant(): 'default' | 'disabled' | 'error' | 'focused' | 'warning' {
		return this.#variant;
	}

	#name: keyof typeof ICONS | null;
	#nameChanged: boolean;
	#variant: 'default' | 'disabled' | 'error' | 'focused' | 'warning';
	#variantChanged: boolean;

	public constructor(name: keyof typeof ICONS | null = null) {
		super();
		this.#name = null;
		this.#nameChanged = false;
		this.width = 18;
		this.height = 18;
		this.viewBox = new ViewBox(0, 0, 18, 18);
		this.name = name;
		this.strokeColor = theme.colors.icon.sub;
		this.strokeWeight = 1.5;
		this.#variant = 'default';
		this.#variantChanged = false;
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'name') {
			this.#nameAttributeChanged(value);
		} else if (name === 'variant') {
			this.#variantAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#nameChanged) {
			this.#commitName();
		}
		if (this.#variantChanged) {
			this.#commitVariant();
		}
	}

	#commitName(): void {
		this.#nameChanged = false;
		if (this.#name === null) {
			this.commands = null;
		} else {
			this.commands = ICONS[this.#name];
		}
	}

	#commitVariant(): void {
		this.#variantChanged = false;
		if (this.variant === 'default') {
			this.strokeColor = theme.colors.icon.sub;
		} else if (this.variant === 'disabled') {
			this.strokeColor = theme.colors.icon.disabled;
		} else if (this.variant === 'error') {
			this.strokeColor = theme.colors.icon.error;
		} else if (this.variant === 'focused') {
			this.strokeColor = theme.colors.icon.focused;
		} else if (this.variant === 'warning') {
			this.strokeColor = theme.colors.icon.warning;
		}
	}

	#nameAttributeChanged(value: string): void {
		assertIsValidIconName(value);
		this.name = value;
	}

	#variantAttributeChanged(value: string): void {
		assertIsValidVariant(value);
		this.variant = value;
	}
}
customElements.define('ac-icon', Icon);

function assertIsValidIconName(value: unknown): asserts value is keyof typeof ICONS | null {
	if ((typeof value === 'string' && value in ICONS) || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string and in ICONS object or null`);
}

function assertIsValidVariant(value: unknown): asserts value is 'default' | 'disabled' | 'error' | 'focused' | 'warning' {
	if (value === 'default' || value === 'disabled' || value === 'error' || value === 'focused' || value === 'warning') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be either "default", "disabled", "error", "focused" or "warning"`);
}
